import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import './EkonomiPage.css';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { ComponentLifetime, TemplatesByType } from '../Förvaltning/TemplatesEditor/types';
import { Building as BuildingType } from '../../types/protocols';
import { ALL_BUILDING_TYPES, BuildingType as BuildingTypeEnum } from '../../types/BuildingTypeEnums';
import { useBuildingTypeFilter } from '../../hooks/useEnabledBuildingTypes';
import {
    generateYearlyPlansForBuilding,
    aggregateYearlyPlans,
    YearlyActivity,
    MaintenancePlan,
    CostCategory,
    generateInspectionCostProjections,
    generateOperatingCostProjections,
    generateLeaseIncomeProjections,
    generateProjectCostProjections,
    mergeYearlyPlans
} from '../../Helpers/maintenanceCostUtils';
import MaintenanceCostsTab from './MaintenanceCostsTab';
import DepreciationTab from './DepreciationTab';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import { useAllOperatingCosts } from '../../hooks/operatingCosts/useAllOperatingCosts';
import { useAllLeases } from '../../hooks/leases';
import { useTenants } from '../../hooks/tenants';
import { useFeature } from '../../hooks/useFeatures';
import { useProjects } from '../../hooks/projects/useProjects';
import { useOperatingCostCategories } from '../../contexts/OperatingCostCategoriesContext';
import { BuildingCheckTemplate } from '../BuildingChecks/types';
import { BuildingCheck, parseBuildingCheck } from '../BuildingChecks/buildingChecksUtils';
import { ALL_FINANCIAL_CATEGORIES } from '../../Helpers/chartColors';
import { loadFromInstanceStorage, saveToInstanceStorage } from '../../Helpers/instanceStorage';
import { useCustomerType } from '../../hooks/useCustomerType';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';

const STORAGE_KEY = 'ekonomiPageSettings';

interface EkonomiPageSettings {
    yearsToShow: number;
    activeTab: 'maintenance' | 'depreciation';
    selectedBuildingTypes: BuildingTypeEnum[];
    adjustForInflation: boolean;
    inflationRate: number;
    selectedCostCategories: CostCategory[];
}

const DEFAULT_SETTINGS: EkonomiPageSettings = {
    yearsToShow: 10,
    activeTab: 'maintenance',
    selectedBuildingTypes: [...ALL_BUILDING_TYPES],
    adjustForInflation: false,
    inflationRate: 2,
    selectedCostCategories: [...ALL_FINANCIAL_CATEGORIES]
};

const loadSettings = (): EkonomiPageSettings => {
    const stored = loadFromInstanceStorage<Partial<EkonomiPageSettings>>(STORAGE_KEY, {});
    const merged = { ...DEFAULT_SETTINGS, ...stored };
    // Drop any persisted categories that no longer exist (e.g. removed 'one-time-cost'),
    // otherwise the cost table renders a headerless column with NaN values.
    const validCategories = new Set<string>(ALL_FINANCIAL_CATEGORIES);
    merged.selectedCostCategories = merged.selectedCostCategories.filter(c => validCategories.has(c));
    return merged;
};

const EkonomiPage: React.FC = () => {
    // Set document title
    useDocumentTitle('Ekonomi');

    // Load saved settings from localStorage
    const [savedSettings] = useState<EkonomiPageSettings>(() => loadSettings());

    const { types: buildComponentTypes, getLifetime, getTemplates } = useBuildComponentTypes();
    const [buildings, setBuildings] = useState<Record<string, BuildingType>>({});
    const [buildingSummaries, setBuildingSummaries] = useState<Record<string, { name?: string; buildingType?: BuildingTypeEnum }>>({});
    const [activePlans, setActivePlans] = useState<Record<string, MaintenancePlan>>({});
    const [componentLifetimes, setComponentLifetimes] = useState<Record<string, ComponentLifetime>>({});
    const [maintenanceTemplates, setMaintenanceTemplates] = useState<TemplatesByType>({});
    const [componentConditions, setComponentConditions] = useState<Record<string, Record<string, any>>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [yearsToShow, setYearsToShow] = useState<number>(savedSettings.yearsToShow);
    const [yearlyPlans, setYearlyPlans] = useState<YearlyActivity[]>([]);
    const [activeTab, setActiveTab] = useState<'maintenance' | 'depreciation'>(savedSettings.activeTab);

    // Avskrivningar is hidden for stiftelser; fall back if it was the saved tab
    const customerType = useCustomerType();
    const showDepreciation = customerType !== 'stiftelse';
    useEffect(() => {
        if (!showDepreciation && activeTab === 'depreciation') {
            setActiveTab('maintenance');
        }
    }, [showDepreciation, activeTab]);

    // Preserve expanded states for depreciation tab
    const [expandedBuildings, setExpandedBuildings] = useState<Set<string>>(new Set());
    const [expandedBaseTypes, setExpandedBaseTypes] = useState<Set<string>>(new Set());

    // Building type filter
    const [selectedBuildingTypes, setSelectedBuildingTypes] = useState<BuildingTypeEnum[]>(savedSettings.selectedBuildingTypes);

    // Inflation adjustment
    const [adjustForInflation, setAdjustForInflation] = useState<boolean>(savedSettings.adjustForInflation);
    const [inflationRate, setInflationRate] = useState<number>(savedSettings.inflationRate);

    // Cost category filter
    const [selectedCostCategories, setSelectedCostCategories] = useState<CostCategory[]>(savedSettings.selectedCostCategories);

    // Inspection data
    const [inspectionTemplates, setInspectionTemplates] = useState<Record<string, BuildingCheckTemplate>>({});
    const [buildingChecks, setBuildingChecks] = useState<Record<string, (BuildingCheck & { checkKey: string })[]>>({});

    // Operating costs from hook
    const {
        costsByBuilding: operatingCostsByBuilding,
        loading: operatingCostsLoading,
        refresh: refreshOperatingCosts
    } = useAllOperatingCosts();

    // Leases (rental income) from hook; tenant names from the lazy tenants context
    const {
        leasesByBuilding,
        loading: leasesLoading
    } = useAllLeases();
    const { tenantsByKey } = useTenants();
    const tenantsEnabled = useFeature('tenants');
    const tenantNames = useMemo(() => {
        const names: Record<string, string> = {};
        for (const [key, tenant] of Object.entries(tenantsByKey)) names[key] = tenant.name;
        return names;
    }, [tenantsByKey]);

    // Projects from hook (budgets contribute to the forecast)
    const {
        projects,
        loading: projectsLoading
    } = useProjects();

    // Save settings to localStorage when they change
    useEffect(() => {
        const settings: EkonomiPageSettings = {
            yearsToShow,
            activeTab,
            selectedBuildingTypes,
            adjustForInflation,
            inflationRate,
            selectedCostCategories
        };
        saveToInstanceStorage(STORAGE_KEY, settings);
    }, [yearsToShow, activeTab, selectedBuildingTypes, adjustForInflation, inflationRate, selectedCostCategories]);

    // Operating cost categories for labels
    const { categories: operatingCostCategories } = useOperatingCostCategories();
    const categoryLabels = useMemo(() => {
        const labels: Record<string, string> = {};
        operatingCostCategories.forEach(cat => {
            labels[cat.key] = cat.label;
        });
        return labels;
    }, [operatingCostCategories]);

    const db = getDatabase();

    // Building type filter: enabled types plus those a building still has
    const typesInUse = useMemo(
        () => Object.values(buildingSummaries).map(summary => summary.buildingType),
        [buildingSummaries],
    );
    const {
        options: buildingTypeOptions,
        selected: visibleBuildingTypes,
        setSelected: setVisibleBuildingTypes,
    } = useBuildingTypeFilter(selectedBuildingTypes, setSelectedBuildingTypes, typesInUse);


    // Load all required data
    const loadData = async (): Promise<void> => {
        try {
            setLoading(true);

            const [buildingsSnapshot, activePlansSnapshot, summariesSnapshot, inspectionTemplatesSnapshot] = await Promise.all([
                get(ref(db, 'buildings')),
                get(ref(db, 'maintenance/active')),
                get(ref(db, 'buildingsSummary')),
                get(ref(db, 'inspections/templates'))
            ]);

            // Load buildings with summaries
            const summariesData = summariesSnapshot.exists() ? summariesSnapshot.val() : {};
            setBuildingSummaries(summariesData);

            if (buildingsSnapshot.exists()) {
                const buildingsData = buildingsSnapshot.val() as Record<string, any>;
                const buildingsWithSummary: Record<string, BuildingType> = {};

                Object.entries(buildingsData).forEach(([buildingKey, buildingModel]) => {
                    buildingsWithSummary[buildingKey] = {
                        key: buildingKey,
                        model: buildingModel,
                        summary: summariesData[buildingKey] || { key: buildingKey }
                    };
                });

                setBuildings(buildingsWithSummary);
            }

            // Load active maintenance plans
            if (activePlansSnapshot.exists()) {
                const activePlansData = activePlansSnapshot.val() as Record<string, MaintenancePlan>;
                setActivePlans(activePlansData);
            }

            // Load component lifetimes and templates from context
            const lifetimesByType: Record<string, ComponentLifetime> = {};
            const templatesByType: TemplatesByType = {};
            
            Object.keys(buildComponentTypes).forEach((type: string) => {
                const lifetime = getLifetime(type);
                if (lifetime) {
                    lifetimesByType[type] = lifetime;
                }
                const templates = getTemplates(type);
                templatesByType[type] = templates;
            });
            
            setComponentLifetimes(lifetimesByType);
            setMaintenanceTemplates(templatesByType);

            // Load component conditions for all buildings with active plans
            const conditionsData: Record<string, Record<string, any>> = {};
            const buildingKeys = Object.keys(activePlans);

            if (buildingKeys.length > 0) {
                const conditionsPromises = buildingKeys.map(async (buildingKey) => {
                    const conditionsSnapshot = await get(ref(db, `componentConditions/${buildingKey}`));
                    if (conditionsSnapshot.exists()) {
                        conditionsData[buildingKey] = conditionsSnapshot.val();
                    } else {
                        conditionsData[buildingKey] = {};
                    }
                });

                await Promise.all(conditionsPromises);
            }

            setComponentConditions(conditionsData);

            // Load inspection templates
            if (inspectionTemplatesSnapshot.exists()) {
                const templatesData = inspectionTemplatesSnapshot.val() as Record<string, BuildingCheckTemplate>;
                setInspectionTemplates(templatesData);
            }

            // Load building checks for all buildings
            const checksData: Record<string, (BuildingCheck & { checkKey: string })[]> = {};
            const allBuildingKeys = Object.keys(buildingsSnapshot.exists() ? buildingsSnapshot.val() : {});

            if (allBuildingKeys.length > 0) {
                const checksPromises = allBuildingKeys.map(async (buildingKey) => {
                    const checksSnapshot = await get(ref(db, `inspections/active/${buildingKey}`));
                    if (checksSnapshot.exists()) {
                        const rawChecks = checksSnapshot.val() as Record<string, unknown>;
                        checksData[buildingKey] = Object.entries(rawChecks).flatMap(([checkKey, data]) => {
                            const check = parseBuildingCheck(checkKey, data);
                            return check ? [{ ...check, checkKey }] : [];
                        });
                    } else {
                        checksData[buildingKey] = [];
                    }
                });

                await Promise.all(checksPromises);
            }

            setBuildingChecks(checksData);

        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Apply inflation adjustment to yearly plans based on when prices were set
    const applyInflationAdjustment = (plans: YearlyActivity[]): YearlyActivity[] => {
        if (!adjustForInflation) {
            return plans;
        }

        const rate = inflationRate / 100; // Convert percentage to decimal

        return plans.map(yearPlan => {
            // Apply inflation to all activities based on their price setting date
            const adjustedActivities = yearPlan.activities.map(activity => {
                // Get the price setting date for this activity
                const priceSettingDate = getPriceSettingDate(activity);
                const priceSettingYear = new Date(priceSettingDate).getFullYear();

                // Calculate years from price setting date to the activity year
                const yearsFromPriceSetting = yearPlan.year - priceSettingYear;
                const inflationMultiplier = Math.pow(1 + rate, yearsFromPriceSetting);

                return {
                    ...activity,
                    cost: activity.cost * inflationMultiplier,
                    originalCost: activity.cost,
                    inflationYears: yearsFromPriceSetting,
                    inflationMultiplier: inflationMultiplier,
                    priceSettingYear: priceSettingYear
                };
            });

            // Recalculate costs with inflation
            const adjustedTotalCost = adjustedActivities.reduce((sum, activity) => sum + activity.cost, 0);

            // Recalculate costs by base type
            const adjustedCostsByBaseType: Record<string, number> = {};
            adjustedActivities.forEach(activity => {
                const baseType = activity.baseType;
                adjustedCostsByBaseType[baseType] = (adjustedCostsByBaseType[baseType] || 0) + activity.cost;
            });

            return {
                ...yearPlan,
                activities: adjustedActivities,
                totalCost: adjustedTotalCost,
                costsByBaseType: adjustedCostsByBaseType
            };
        });
    };

    // Helper function to get the price setting date for an activity
    // This ensures inflation is calculated from when the price was actually set,
    // not from the current year, providing more accurate cost projections
    const getPriceSettingDate = (activity: any): string => {
        // For maintenance activities, use the lastUpdated timestamp from the maintenance plan
        if (activity.activityType === 'maintenance' && activity.templateId) {
            const buildingKey = activity.buildingKey;
            const componentKey = activity.componentKey;
            const templateId = activity.templateId;

            if (buildingKey && activePlans[buildingKey]?.components[componentKey]?.maintenanceActivities?.[templateId]?.lastUpdated) {
                return activePlans[buildingKey].components[componentKey].maintenanceActivities[templateId].lastUpdated;
            }
        }

        // For replacement activities, use the plan's savedAt date
        if (activity.activityType === 'replacement' && activity.buildingKey) {
            const buildingKey = activity.buildingKey;
            if (activePlans[buildingKey]?.metadata?.savedAt) {
                return activePlans[buildingKey].metadata.savedAt;
            }
        }

        // Fallback to current date if no timestamp is available
        return new Date().toISOString();
    };

    // Generate aggregated yearly plans from all active maintenance plans, inspections, and operating costs
    const generateAggregatedYearlyPlans = (): void => {
        // We can proceed even without maintenance plans if we have inspections or operating costs
        if (!Object.keys(buildings).length) {
            return;
        }

        const allMaintenancePlans: YearlyActivity[][] = [];
        const allInspectionPlans: YearlyActivity[][] = [];
        const allOperatingCostPlans: YearlyActivity[][] = [];
        const allLeaseIncomePlans: YearlyActivity[][] = [];

        // Process each building
        Object.entries(buildings).forEach(([buildingKey, building]) => {
            // Filter by selected building types
            if (building.summary?.buildingType && !selectedBuildingTypes.includes(building.summary.buildingType)) {
                return;
            }

            const buildingName = building.summary?.name || 'Namnlös byggnad';

            // Generate maintenance plans (if building has an active plan)
            const activePlan = activePlans[buildingKey];
            if (activePlan && building?.model?.structure?.components && Object.keys(componentLifetimes).length && Object.keys(maintenanceTemplates).length) {
                const buildingConditions = componentConditions[buildingKey] || {};

                const maintenancePlans = generateYearlyPlansForBuilding({
                    building,
                    plan: activePlan,
                    componentLifetimes,
                    maintenanceTemplates,
                    componentConditions: buildingConditions,
                    yearsToShow,
                    buildingName,
                    buildComponentTypes
                });

                if (maintenancePlans.length > 0) {
                    allMaintenancePlans.push(maintenancePlans);
                }
            }

            // Generate inspection cost projections
            const checks = buildingChecks[buildingKey] || [];
            if (checks.length > 0 && Object.keys(inspectionTemplates).length > 0) {
                const inspectionPlans = generateInspectionCostProjections(
                    checks,
                    inspectionTemplates,
                    buildingKey,
                    buildingName,
                    yearsToShow
                );

                if (inspectionPlans.length > 0) {
                    allInspectionPlans.push(inspectionPlans);
                }
            }

            // Generate operating cost projections
            const operatingCosts = operatingCostsByBuilding[buildingKey] || [];
            if (operatingCosts.length > 0) {
                const operatingPlans = generateOperatingCostProjections(
                    operatingCosts,
                    buildingKey,
                    buildingName,
                    yearsToShow,
                    categoryLabels
                );

                if (operatingPlans.length > 0) {
                    allOperatingCostPlans.push(operatingPlans);
                }
            }

            // Generate lease income projections
            // No rental income while renting is off (Inställningar → Funktioner)
            const buildingLeases = tenantsEnabled ? leasesByBuilding[buildingKey] || [] : [];
            if (buildingLeases.length > 0) {
                const leaseIncomePlans = generateLeaseIncomeProjections(
                    buildingLeases,
                    tenantNames,
                    buildingKey,
                    buildingName,
                    yearsToShow
                );

                if (leaseIncomePlans.length > 0) {
                    allLeaseIncomePlans.push(leaseIncomePlans);
                }
            }

        });

        // Generate project budget projections (one full-window array covering all
        // projects with a budget whose building matches the selected types).
        const projectsForForecast = projects.filter(project =>
            project.budget != null &&
            (!project.buildingType || selectedBuildingTypes.includes(project.buildingType))
        );
        const projectPlans = projectsForForecast.length > 0
            ? generateProjectCostProjections(projectsForForecast, yearsToShow)
            : [];

        // Aggregate each type of plan separately
        const aggregatedMaintenance = aggregateYearlyPlans(allMaintenancePlans);
        const aggregatedInspections = aggregateYearlyPlans(allInspectionPlans);
        const aggregatedOperatingCosts = aggregateYearlyPlans(allOperatingCostPlans);
        const aggregatedLeaseIncome = aggregateYearlyPlans(allLeaseIncomePlans);

        // Merge all plans together. mergeYearlyPlans is year-keyed and variadic, so
        // the project plans join as one more source (already aggregated by the generator).
        const mergedPlans = mergeYearlyPlans(
            aggregatedMaintenance,
            aggregatedInspections,
            aggregatedOperatingCosts,
            aggregatedLeaseIncome,
            projectPlans
        );

        // Apply inflation adjustment if enabled
        const finalPlans = applyInflationAdjustment(mergedPlans);

        setYearlyPlans(finalPlans);
    };

    // Load data when component mounts
    useEffect(() => {
        loadData();
    }, []);

    // Initialize Bootstrap tooltips
    useEffect(() => {
        const initializeTooltips = () => {
            // Check if Bootstrap is available
            if (typeof window !== 'undefined' && (window as any).bootstrap && (window as any).bootstrap.Tooltip) {
                // Initialize tooltips when component mounts or updates
                const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
                const tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
                    return new (window as any).bootstrap.Tooltip(tooltipTriggerEl, {
                        container: 'body', // This ensures tooltips are appended to body, avoiding clipping
                        boundary: 'viewport'
                    });
                });

                return tooltipList;
            }
            return [];
        };

        // Try to initialize immediately
        let tooltipList = initializeTooltips();

        // If Bootstrap isn't available yet, wait a bit and try again
        if (tooltipList.length === 0) {
            const timeoutId = setTimeout(() => {
                tooltipList = initializeTooltips();
            }, 100);

            return () => {
                clearTimeout(timeoutId);
                tooltipList.forEach(tooltip => tooltip.dispose());
            };
        }

        // Cleanup tooltips when component unmounts
        return () => {
            tooltipList.forEach(tooltip => tooltip.dispose());
        };
    }, [yearlyPlans]); // Re-initialize when yearly plans change

    // Generate yearly plans when dependencies change
    useEffect(() => {
        generateAggregatedYearlyPlans();
    }, [buildings, activePlans, componentLifetimes, maintenanceTemplates, componentConditions, yearsToShow, selectedBuildingTypes, adjustForInflation, inflationRate, buildComponentTypes, inspectionTemplates, buildingChecks, operatingCostsByBuilding, leasesByBuilding, tenantsEnabled, tenantNames, projects, categoryLabels]);

    if (loading) {
        return (
            <div className="ekonomi-page">
                <div className="loading-container">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                </div>
            </div>
        );
    }

    const totalActivities = yearlyPlans.reduce((total, year) => total + year.activities.length, 0);
    const totalCost = yearlyPlans.reduce((total, year) => total + year.totalCost, 0);

    // Count only buildings that match the selected building types and have active plans
    const activeBuildingsCount = Object.keys(activePlans).filter(buildingKey => {
        const building = buildings[buildingKey];
        return building?.summary?.buildingType && selectedBuildingTypes.includes(building.summary.buildingType);
    }).length;

    return (
        <div className="ekonomi-page">
            <BreadcrumbComponent items={[{ text: 'Ekonomi' }]} />

            {/* Page header: title with the view tabs underneath, using the same
                underline tab style as Inställningar and the component list pages.
                The bar is hidden when there is only one view (Avskrivningar is not
                shown to stiftelser), since a single tab has nothing to switch to. */}
            <div className="ekonomi-header">
                <h1 className="ekonomi-title page-title">Ekonomi</h1>
                {showDepreciation && (
                    <div className="ekonomi-tab-bar" role="tablist" aria-label="Ekonomivy">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'maintenance'}
                            className={`ekonomi-tab ${activeTab === 'maintenance' ? 'active' : ''}`}
                            onClick={() => setActiveTab('maintenance')}
                        >
                            Kostnadsprognos
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'depreciation'}
                            className={`ekonomi-tab ${activeTab === 'depreciation' ? 'active' : ''}`}
                            onClick={() => setActiveTab('depreciation')}
                        >
                            Avskrivningar
                        </button>
                    </div>
                )}
            </div>

            {/* Tab Content */}
            <div className="tab-content">
                {activeTab === 'maintenance' && (
                    <MaintenanceCostsTab
                        buildings={buildings}
                        buildingSummaries={buildingSummaries}
                        activePlans={activePlans}
                        componentLifetimes={componentLifetimes}
                        maintenanceTemplates={maintenanceTemplates}
                        componentConditions={componentConditions}
                        loading={loading || operatingCostsLoading || leasesLoading || projectsLoading}
                        yearsToShow={yearsToShow}
                        setYearsToShow={setYearsToShow}
                        yearlyPlans={yearlyPlans}
                        selectedBuildingTypes={visibleBuildingTypes}
                        setSelectedBuildingTypes={setVisibleBuildingTypes}
                        adjustForInflation={adjustForInflation}
                        setAdjustForInflation={setAdjustForInflation}
                        inflationRate={inflationRate}
                        setInflationRate={setInflationRate}
                        buildingTypeOptions={buildingTypeOptions}
                        selectedCostCategories={selectedCostCategories}
                        setSelectedCostCategories={setSelectedCostCategories}
                        onOperatingCostUpdated={refreshOperatingCosts}
                    />
                )}

                {activeTab === 'depreciation' && showDepreciation && (
                    <DepreciationTab
                        buildings={buildings}
                        componentLifetimes={componentLifetimes}
                        activePlans={activePlans}
                        onDataRefresh={loadData}
                        expandedBuildings={expandedBuildings}
                        setExpandedBuildings={setExpandedBuildings}
                        expandedBaseTypes={expandedBaseTypes}
                        setExpandedBaseTypes={setExpandedBaseTypes}
                    />
                )}
            </div>
        </div>
    );
};

export default EkonomiPage;
